/**
 * The faces the Coast app sets itself and its views in (docs/views.md#4-layouts), from design/tokens.json: DM Sans,
 * the default, Geist, Hanken Grotesk, Inter, Cabin, Noto Sans, Gotu, and Jua, each with its monospace. Their files are the view's server's, at
 * `/fonts/<file>` (build.mjs copies them from `design/assets/fonts`, with their SIL Open Font Licenses), so a page in
 * the Coast app and a picture the server draws of it are set alike. A page gets the face as `--coast-font` and
 * `--coast-font-mono`, and as `coast.font`.
 */
import typeScale from "../generated/type.json";

export const FONT_FACES = typeScale.faces;
export type Face = keyof typeof FONT_FACES;
export const isFace = (value: unknown): value is Face => typeof value === "string" && Object.hasOwn(FONT_FACES, value);
/** The face a page is set in until the Coast app says which its person chose, and in a picture the server draws. */
export const DEFAULT_FACE = Object.keys(FONT_FACES)[0] as Face;

/** One file of a family's, at `/fonts/<file>`: the weights it draws, and whether it is the italic. */
export interface FontFile {
  file: string;
  weight: string;
  italic?: true;
}
/** Each family a face names, sans and mono, with its files; a family with one weight is drawn bolder by the browser. */
export const FONT_FILES: Record<string, readonly FontFile[]> = {
  "DM Sans": [
    { file: "DMSans-Variable.woff2", weight: "100 1000" },
    { file: "DMSans-Italic-Variable.woff2", weight: "100 1000", italic: true },
  ],
  "Hanken Grotesk": [
    { file: "HankenGrotesk-Variable.woff2", weight: "100 900" },
    { file: "HankenGrotesk-Italic-Variable.woff2", weight: "100 900", italic: true },
  ],
  Geist: [{ file: "Geist-Variable.woff2", weight: "100 900" }],
  "Geist Mono": [{ file: "GeistMono-Variable.woff2", weight: "100 900" }],
  Jua: [{ file: "Jua-Regular.woff2", weight: "400" }],
  Cabin: [{ file: "Cabin-Variable.woff2", weight: "400 700" }],
  Gotu: [{ file: "Gotu-Regular.woff2", weight: "400" }],
  "Noto Sans": [{ file: "NotoSans-Variable.woff2", weight: "100 900" }],
  Inter: [{ file: "Inter-Variable.woff2", weight: "100 900" }],
  "JetBrains Mono": [
    { file: "JetBrainsMono-Variable.woff2", weight: "100 800" },
    { file: "JetBrainsMono-Italic-Variable.woff2", weight: "100 800", italic: true },
  ],
};
/** The families a stack names: `"Jua", "DM Sans", sans-serif` is Jua and DM Sans. */
export const familiesOf = (stack: string) => [...stack.matchAll(/"([^"]+)"/g)].map((match) => match[1]!);

/** The font as a page reads it, `coast.font`: the face and its stacks. */
export const fontOf = (face: Face) => ({ face, family: FONT_FACES[face].family, mono: FONT_FACES[face].mono });

/**
 * The CSS a page starts with for `face`: its files from `server`, with the default face's, which a character the face
 * lacks falls back to and initials are always set in (Issues #48); the variables; and the rule that sets it on the page
 * and its controls over any `font-family` of the view's, unless its manifest says `font: "own"`
 * (docs/spec/view-format.md#23-how-it-shows, M10).
 */
export function fontStyle(face: Face, server: string, own: boolean): string {
  const { family, mono } = FONT_FACES[face];
  const faces = fontFaces([family, mono, FONT_FACES[DEFAULT_FACE].family].flatMap(familiesOf), server);
  const variables = `:root{--coast-font:${family};--coast-font-mono:${mono}}`;
  const rule = own
    ? ""
    : "*,*::before,*::after{font-family:var(--coast-font)!important}code,kbd,pre,samp,code *,pre *{font-family:var(--coast-font-mono)!important}";
  return faces + variables + rule;
}

/** The `@font-face` rules of `families`, each once, from the files `server` serves at `/fonts`. */
export function fontFaces(families: string[], server: string): string {
  return [...new Set(families)]
    .flatMap((name) =>
      (FONT_FILES[name] ?? []).map(
        (entry) =>
          `@font-face{font-family:"${name}";src:url("${server}/fonts/${entry.file}") format("woff2");font-weight:${entry.weight};font-style:${entry.italic ? "italic" : "normal"};font-display:swap}`,
      ),
    )
    .join("");
}

/**
 * The roles of Coast's type scale (design/tokens.json, docs/views.md#4-layouts), each one of the platform's text styles:
 * a page's `--coast-size-<role>` in CSS pixels, which are points in the Coast app's web views.
 */
export type TextRole = keyof typeof typeScale.scale;
export type TextSizes = Record<TextRole, number>;
const ROLES = Object.keys(typeScale.scale) as TextRole[];
/** What a page sizes its text by: each role's size, and `scale`, the body's against the platform's at its default. */
export interface PageText {
  sizes: TextSizes;
  scale: number;
}
/**
 * The sizes Apple gives the roles at the default text size: the Mac's for a page in the desktop layout, iPhone's for the
 * rest, for a page no Coast app gives its own, such as a render's.
 */
export function defaultText(platform: "ios" | "mac"): PageText {
  return {
    sizes: Object.fromEntries(ROLES.map((role) => [role, typeScale.scale[role][platform]])) as TextSizes,
    scale: 1,
  };
}
/**
 * Text sizes as the Coast app sends them (`setTextSizes`), checked: every role a number of points between 6 and 120,
 * which Dynamic Type's largest accessibility sizes stay within, and the scale between a quarter and eight.
 */
export function pageTextOf(value: unknown): PageText | null {
  if (!value || typeof value !== "object") return null;
  const { sizes, scale } = value as { sizes?: Record<string, unknown>; scale?: unknown };
  if (!sizes || typeof sizes !== "object" || typeof scale !== "number" || !(scale >= 0.25 && scale <= 8)) return null;
  const checked: Partial<TextSizes> = {};
  for (const role of ROLES) {
    const size = sizes[role];
    if (typeof size !== "number" || !(size >= 6 && size <= 120)) return null;
    checked[role] = Math.round(size * 100) / 100;
  }
  return { sizes: checked as TextSizes, scale: Math.round(scale * 1000) / 1000 };
}
/**
 * A page's text sizes as CSS, `--coast-size-<role>` and `--coast-text-scale`, on `:root:root`, which outranks the stock
 * style's own defaults on `:root` though this rule comes before it.
 */
export function textStyle({ sizes, scale }: PageText): string {
  return `:root:root{${ROLES.map((role) => `--coast-size-${role}:${sizes[role]}px;`).join("")}--coast-text-scale:${scale}}`;
}
